import React, { Suspense, lazy } from 'react';
import { Routes, Route, useLocation, useOutlet, Navigate, useParams } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Navbar } from './components/Layout/Navbar';
import { Footer } from './components/Layout/Footer';
import { Meta } from './components/SEO/Meta';
import { RequireAuth } from './components/Auth/RequireAuth';
import { LazyChatWidget } from './components/Chat/LazyChatWidget';
import { ScrollToTop } from './components/System/ScrollToTop';
import { PageTransition } from './components/System/PageTransition';
import { isLang, DEFAULT_LANG } from './i18n/locales';
import { CartDrawer } from './components/Cart/CartDrawer';

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-natuvia-cta border-r-transparent"></div>
      <p className="mt-2 text-sm text-natuvia-text/60">Loading...</p>
    </div>
  </div>
);

// Lazy-loaded Pages
const Home = lazy(() => import('./pages/Home').then(m => ({ default: m.Home })));
const About = lazy(() => import('./pages/About').then(m => ({ default: m.About })));
const Products = lazy(() => import('./pages/Products').then(m => ({ default: m.Products })));
const ProductDetail = lazy(() =>
  import('./pages/ProductDetail').then(m => ({ default: m.ProductDetail }))
);
const Shop = lazy(() => import('./pages/Shop').then(m => ({ default: m.Shop })));
const Cart = lazy(() => import('./pages/Cart').then(m => ({ default: m.Cart })));
const Checkout = lazy(() => import('./pages/Checkout').then(m => ({ default: m.Checkout })));
const OrderConfirmation = lazy(() =>
  import('./pages/OrderConfirmation').then(m => ({ default: m.OrderConfirmation }))
);
const OrderHistory = lazy(() =>
  import('./pages/OrderHistory').then(m => ({ default: m.OrderHistory }))
);
const Journal = lazy(() => import('./pages/Journal').then(m => ({ default: m.Journal })));
const JournalPost = lazy(() =>
  import('./pages/JournalPost').then(m => ({ default: m.JournalPost }))
);
const FAQ = lazy(() => import('./pages/FAQ').then(m => ({ default: m.FAQ })));
const Contact = lazy(() => import('./pages/Contact').then(m => ({ default: m.Contact })));
const Shipping = lazy(() => import('./pages/Shipping').then(m => ({ default: m.Shipping })));
const Privacy = lazy(() => import('./pages/Privacy').then(m => ({ default: m.Privacy })));
const Terms = lazy(() => import('./pages/Terms').then(m => ({ default: m.Terms })));
const StyleGuide = lazy(() => import('./pages/StyleGuide').then(m => ({ default: m.StyleGuide })));
const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })));
const Account = lazy(() => import('./pages/Account').then(m => ({ default: m.Account })));
const PetProfile = lazy(() => import('./pages/PetProfile').then(m => ({ default: m.PetProfile })));
const PetServices = lazy(() =>
  import('./pages/PetServices').then(m => ({ default: m.PetServices }))
);

// Lazy-loaded Admin Pages
const AdminGateLayout = lazy(() =>
  import('./components/Admin/AdminGateLayout').then(m => ({ default: m.AdminGateLayout }))
);
const AdminHome = lazy(() => import('./pages/AdminHome').then(m => ({ default: m.AdminHome })));
const AdminJournal = lazy(() =>
  import('./pages/AdminJournal').then(m => ({ default: m.AdminJournal }))
);
const AdminJournalEdit = lazy(() =>
  import('./pages/AdminJournalEdit').then(m => ({ default: m.AdminJournalEdit }))
);
const AdminOrders = lazy(() =>
  import('./pages/AdminOrders').then(m => ({ default: m.AdminOrders }))
);
const AdminProducts = lazy(() =>
  import('./pages/AdminProducts').then(m => ({ default: m.AdminProducts }))
);
const AdminProductEdit = lazy(() =>
  import('./pages/AdminProductEdit').then(m => ({ default: m.AdminProductEdit }))
);
const AdminInventory = lazy(() =>
  import('./pages/AdminInventory').then(m => ({ default: m.AdminInventory }))
);
const AdminReviews = lazy(() =>
  import('./pages/AdminReviews').then(m => ({ default: m.AdminReviews }))
);
const AdminVisibility = lazy(() =>
  import('./pages/AdminVisibility').then(m => ({ default: m.AdminVisibility }))
);
const AdminChats = lazy(() => import('./pages/AdminChats').then(m => ({ default: m.AdminChats })));
const AdminPrompts = lazy(() =>
  import('./pages/AdminPrompts').then(m => ({ default: m.AdminPrompts }))
);
const AdminNewsletter = lazy(() =>
  import('./pages/AdminNewsletter').then(m => ({ default: m.AdminNewsletter }))
);
const AdminStylePreview = lazy(() =>
  import('./pages/AdminStylePreview').then(m => ({ default: m.AdminStylePreview }))
);

// 1. Language Guard / Layout Shell
const LangLayout = () => {
  const { lang } = useParams();
  const location = useLocation();
  const outlet = useOutlet();

  // Guard: If we somehow match "admin" or reserved words here, do NOT redirect.
  const reserved = ['admin', 'api', 'assets', 'favicon.ico', 'robots.txt', 'sitemap.xml'];
  if (lang && reserved.includes(lang.toLowerCase())) {
    return null;
  }

  // If URL prefix is not a valid lang, redirect to EN + path
  if (!isLang(lang)) {
    const pathParts = location.pathname.split('/').slice(2).join('/');
    return <Navigate to={`/${DEFAULT_LANG}/${pathParts}`} replace />;
  }

  return (
    <div className="min-h-screen flex flex-col font-sans text-natuvia-text bg-natuvia-primary selection:bg-natuvia-cta selection:text-white">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[9999] focus:bg-natuvia-cta focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:font-bold"
      >
        Skip to main content
      </a>
      <Navbar />
      <CartDrawer />
      <LazyChatWidget />
      <main id="main-content" className="flex-grow" role="main">
        <AnimatePresence mode="wait">
          <PageTransition key={location.pathname}>{outlet}</PageTransition>
        </AnimatePresence>
      </main>
      <Footer />
    </div>
  );
};

export default function App() {
  return (
    <>
      <Meta />
      <ScrollToTop />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* 1. Root: Redirect to /en */}
          <Route path="/" element={<Navigate to={`/${DEFAULT_LANG}`} replace />} />

          {/* 2. ADMIN ROUTES - Top Priority */}
          <Route path="/admin" element={<AdminGateLayout />}>
            <Route index element={<AdminHome />} />
            <Route path="journal" element={<AdminJournal />} />
            <Route path="journal/new" element={<AdminJournalEdit />} />
            <Route path="journal/edit/:id" element={<AdminJournalEdit />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="products/new" element={<AdminProductEdit />} />
            <Route path="products/edit/:id" element={<AdminProductEdit />} />
            <Route path="inventory" element={<AdminInventory />} />
            <Route path="reviews" element={<AdminReviews />} />
            <Route path="visibility" element={<AdminVisibility />} />
            <Route path="chats" element={<AdminChats />} />
            <Route path="prompts" element={<AdminPrompts />} />
            <Route path="newsletter" element={<AdminNewsletter />} />
            <Route path="style-preview" element={<AdminStylePreview />} />
          </Route>

          {/* 3. Main Localized Routes */}
          <Route path="/:lang" element={<LangLayout />}>
            <Route index element={<Home />} />
            <Route path="about" element={<About />} />
            <Route path="products" element={<Products />} />
            <Route path="products/:slug" element={<ProductDetail />} />
            <Route path="shop" element={<Shop />} /> {/* Registered Shop Route */}
            <Route path="cart" element={<Cart />} />
            <Route path="checkout" element={<Checkout />} />
            <Route path="order-confirmation" element={<OrderConfirmation />} />
            <Route
              path="order-history"
              element={
                <RequireAuth>
                  <OrderHistory />
                </RequireAuth>
              }
            />
            <Route path="journal" element={<Journal />} />
            <Route path="journal/:slug" element={<JournalPost />} />
            <Route path="faq" element={<FAQ />} />
            <Route path="pet-services" element={<PetServices />} />
            <Route path="contact" element={<Contact />} />
            <Route path="shipping" element={<Shipping />} />
            <Route path="privacy" element={<Privacy />} />
            <Route path="terms" element={<Terms />} />
            <Route path="style-guide" element={<StyleGuide />} />
            <Route path="login" element={<Login />} />
            <Route
              path="account"
              element={
                <RequireAuth>
                  <Account />
                </RequireAuth>
              }
            />
            <Route
              path="pet/:petId"
              element={
                <RequireAuth>
                  <PetProfile />
                </RequireAuth>
              }
            />
          </Route>

          {/* 4. Auth Callback & Fallbacks */}
          <Route path="/auth/callback" element={<Navigate to={`/${DEFAULT_LANG}`} replace />} />
          <Route path="*" element={<Navigate to={`/${DEFAULT_LANG}`} replace />} />
        </Routes>
      </Suspense>
    </>
  );
}
